// Iter311 · G01 — "Mi trabajo": bandeja por perfil con vencimientos,
// aprobaciones, bloqueos y siguiente acción. Para que el usuario identifique
// y abra su tarea prioritaria sin tener que buscar entre los módulos —
// cada elemento muestra dueño, fecha y enlace directo (criterio de
// aceptación G01 del informe de mejoras).
// Iter313 · G04 (accesos rápidos) — los tres tipos de elemento accionable
// (confirmar presentación, validar TIC, aprobar CFO) se pueden resolver
// sin salir de la bandeja: botón directo o modal inline reutilizando los
// mismos componentes/endpoints que OfferDetail / OfertaForm.
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Loader2, ListChecks, ArrowRight, AlertCircle, Clock3, CircleDot, Check, ChevronDown, ChevronRight } from "lucide-react";
import { ItValidateModal } from "@/components/offer/ItValidateModal";
import { CfoApproveModal } from "@/components/offer/CfoApproveModal";

const URGENCIA_META = {
  rojo:     { dot: "bg-rose-500",   label: "Urgente" },
  amarillo: { dot: "bg-amber-500",  label: "Esta semana" },
  verde:    { dot: "bg-green-500",  label: "A tiempo" },
  gris:     { dot: "bg-gray-400",   label: "Sin fecha" },
};

const TIPO_ICON = {
  confirmacion_presentacion: AlertCircle,
  validacion_tic: ListChecks,
  aprobacion_cfo: ListChecks,
  checklist_implantacion: Clock3,
  it_peticion: CircleDot,
  traspaso_pendiente_aceptacion: AlertCircle,
};

const RESOLVABLE_TIPOS = new Set(["confirmacion_presentacion", "validacion_tic", "aprobacion_cfo"]);

// T11 (tanda integral 12/10/2026) · pestañas de ámbito y fecha para no
// depender sólo del orden por urgencia — "mío" se calcula en el servidor
// comparando contra el nombre/email del usuario conectado.
const SCOPE_TABS = [
  { key: "todos", label: "Todos" },
  { key: "mio", label: "Mío" },
  { key: "equipo", label: "Equipo" },
  { key: "sin_asignar", label: "Sin asignar" },
];
const FECHA_TABS = [
  { key: "todos", label: "Todas" },
  { key: "vencido", label: "Vencido" },
  { key: "hoy", label: "Hoy" },
  { key: "proximo", label: "Próximo" },
  { key: "sin_fecha", label: "Sin fecha" },
];

function fmtFecha(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

export default function MiTrabajo() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [itForm, setItForm] = useState({ action: "approve", comments: "", conditions: "" });
  const [cfoForm, setCfoForm] = useState({ action: "approve", comments: "", conditions: "", reject_category: "" });
  const [resolving, setResolving] = useState(false);
  const [scope, setScope] = useState("todos");
  const [fechaBucket, setFechaBucket] = useState("todos");
  // T11 · la sección de revisión masiva (previsiones/evidencias/notas de
  // Comité, que pueden llegar a decenas) arranca PLEGADA para que no tape
  // las pocas acciones directas que sí requieren una decisión ahora mismo.
  const [revisionAbierta, setRevisionAbierta] = useState(false);

  const load = (s = scope, f = fechaBucket) => {
    axios.get(`${API}/mi-trabajo/resumen`, { params: { scope: s, fecha_bucket: f } })
      .then(r => { setData(r.data); setError(null); })
      .catch(() => setError("No se pudo cargar tu bandeja de trabajo."));
  };
  useEffect(() => { load(); }, []);

  const handleScopeChange = (s) => { setScope(s); load(s, fechaBucket); };
  const handleFechaChange = (f) => { setFechaBucket(f); load(scope, f); };

  const toggleExpand = (key) => {
    setExpandedIdx(prev => prev === key ? null : key);
    setItForm({ action: "approve", comments: "", conditions: "" });
    setCfoForm({ action: "approve", comments: "", conditions: "", reject_category: "" });
  };

  // ── Confirmar presentación en un clic ("Sí, se presentó") ──────────────
  const handleConfirmPresentada = async (item, e) => {
    e.stopPropagation();
    setResolving(true);
    try {
      await axios.post(`${API}/ofertas-comercial/${item.id}/resolve-confirmacion-presentacion`, { presentada: true });
      toast.success("Presentación confirmada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo confirmar. Ábrela desde Licitaciones.");
    } finally { setResolving(false); }
  };

  // ── Validación TIC inline ───────────────────────────────────────────────
  const handleItValidate = async (item) => {
    if (itForm.action === "reject" && !itForm.comments.trim()) {
      toast.error("Debes añadir un comentario al rechazar"); return;
    }
    if (itForm.action === "approve_with_conditions" && !(itForm.conditions || "").trim()) {
      toast.error("Debes indicar las condiciones exigidas"); return;
    }
    setResolving(true);
    try {
      await axios.post(`${API}/offers/${item.id}/validate`, itForm);
      toast.success("Validación registrada");
      setExpandedIdx(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al validar"); }
    finally { setResolving(false); }
  };

  // ── Aprobación CFO inline ───────────────────────────────────────────────
  const handleCfoApprove = async (item) => {
    if (cfoForm.action === "reject" && (!cfoForm.comments.trim() || !cfoForm.reject_category)) {
      toast.error("Debes añadir comentario y categoría al rechazar"); return;
    }
    if (cfoForm.action === "approve_with_conditions" && !(cfoForm.conditions || "").trim()) {
      toast.error("Debes indicar las condiciones exigidas"); return;
    }
    setResolving(true);
    try {
      await axios.post(`${API}/offers/${item.id}/cfo-approve`, cfoForm);
      toast.success("Decisión CFO registrada");
      setExpandedIdx(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error en la aprobación CFO"); }
    finally { setResolving(false); }
  };

  if (error) {
    return <div className="p-6 text-sm text-rose-600" data-testid="mi-trabajo-error">{error}</div>;
  }
  if (!data) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-gray-500" data-testid="mi-trabajo-loading">
        <Loader2 className="animate-spin" size={16}/> Cargando tu trabajo pendiente…
      </div>
    );
  }

  const { items, total, counts_scope, counts_fecha_bucket } = data;
  const accionDirecta = items.filter(i => i.grupo === "accion_directa");
  const revisionMasiva = items.filter(i => i.grupo === "revision_masiva");

  const renderItemCard = (it, key) => {
    const Icon = TIPO_ICON[it.tipo] || ListChecks;
    const um = URGENCIA_META[it.urgencia] || URGENCIA_META.gris;
    const fecha = fmtFecha(it.fecha);
    const resolvable = RESOLVABLE_TIPOS.has(it.tipo) && it.id;
    const expanded = expandedIdx === key;
    return (
      <div key={key} className="bg-white border border-gray-200 rounded-xl overflow-hidden transition-all"
           data-testid={`mi-trabajo-item-${key}`}>
        <div
          onClick={() => resolvable ? toggleExpand(key) : navigate(it.enlace)}
          className="w-full text-left p-4 flex items-center gap-3 hover:bg-gray-50 cursor-pointer transition-all group"
        >
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${um.dot}`} title={um.label} data-testid={`mi-trabajo-urgencia-${key}`}/>
          <Icon size={16} className="text-gray-400 flex-shrink-0"/>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase font-semibold tracking-wide text-gray-400">{it.tipo_label}</p>
            <p className="text-sm font-semibold text-gray-900 truncate">{it.titulo}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {it.dueno}{fecha ? ` · ${fecha}` : ""}
            </p>
            {/* Reglas de negocio v1, sección 7 (12/10/2026): "resultado
                esperado" + "vencimiento o razón de ausencia" explícitos
                — el vencimiento calculado NUNCA se presenta como fecha
                contractual. */}
            {it.resultado_esperado && (
              <p className="text-[11px] text-gray-400 mt-0.5 truncate" data-testid={`mi-trabajo-resultado-${key}`}>
                → {it.resultado_esperado}
              </p>
            )}
            {it.vencimiento_objetivo && it.vencimiento_tipo === "objetivo_interno_calculado" && (
              <span className="inline-block text-[10px] text-blue-600 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5 mt-1" data-testid={`mi-trabajo-vencimiento-${key}`}
                    title="Objetivo interno calculado por la regla de negocio v1 — no es una fecha contractual">
                Objetivo interno: {fmtFecha(it.vencimiento_objetivo)}
              </span>
            )}
          </div>
          {it.tipo === "confirmacion_presentacion" && it.id && (
            <button
              onClick={(e) => handleConfirmPresentada(it, e)}
              disabled={resolving}
              data-testid={`mi-trabajo-resolve-${key}`}
              className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold bg-green-50 text-green-700 border border-green-200 rounded-lg px-2.5 py-1.5 hover:bg-green-100 disabled:opacity-50"
            >
              <Check size={12}/> Sí, se presentó
            </button>
          )}
          {(it.tipo === "validacion_tic" || it.tipo === "aprobacion_cfo") && it.id && (
            <button
              data-testid={`mi-trabajo-resolve-${key}`}
              className="flex-shrink-0 text-xs font-semibold text-[#007934] border border-[#007934]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#007934]/5"
            >
              {expanded ? "Cerrar" : "Resolver"}
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); navigate(it.enlace); }}
            title="Abrir ficha completa" data-testid={`mi-trabajo-open-${key}`}
            className="flex-shrink-0 p-1">
            <ArrowRight size={16} className="text-gray-300 group-hover:text-[#007934] transition-colors"/>
          </button>
        </div>
        {expanded && it.tipo === "validacion_tic" && (
          <div className="px-4 pb-4">
            <ItValidateModal open form={itForm} onFormChange={setItForm}
              onCancel={() => setExpandedIdx(null)} onConfirm={() => handleItValidate(it)} loading={resolving}/>
          </div>
        )}
        {expanded && it.tipo === "aprobacion_cfo" && (
          <div className="px-4 pb-4">
            <CfoApproveModal open form={cfoForm} onFormChange={setCfoForm}
              onCancel={() => setExpandedIdx(null)} onConfirm={() => handleCfoApprove(it)} loading={resolving}/>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="mi-trabajo-page">
      <div>
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <ListChecks className="text-[#007934]" size={22}/> Mi trabajo
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {total === 0
            ? "No tienes elementos pendientes ahora mismo. 🎉"
            : `${total} elemento${total === 1 ? "" : "s"} pendiente${total === 1 ? "" : "s"} que necesitan tu atención.`}
        </p>
      </div>

      {/* T11 · pestañas de ámbito y fecha — los contadores vienen del
          backend sobre el conjunto SIN filtrar, para que no cambien al
          cambiar de pestaña. */}
      <div className="flex flex-wrap gap-4" data-testid="mi-trabajo-filtros">
        <div className="flex flex-wrap gap-1.5" data-testid="mi-trabajo-filtro-scope">
          {SCOPE_TABS.map(t => (
            <button key={t.key} onClick={() => handleScopeChange(t.key)}
              data-testid={`mi-trabajo-scope-${t.key}`}
              className={`text-xs font-semibold rounded-full px-3 py-1 border transition-colors ${
                scope === t.key ? "bg-[#007934] text-white border-[#007934]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
              }`}>
              {t.label}{t.key !== "todos" && counts_scope ? ` (${counts_scope[t.key] ?? 0})` : ""}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="mi-trabajo-filtro-fecha">
          {FECHA_TABS.map(t => (
            <button key={t.key} onClick={() => handleFechaChange(t.key)}
              data-testid={`mi-trabajo-fecha-${t.key}`}
              className={`text-xs font-semibold rounded-full px-3 py-1 border transition-colors ${
                fechaBucket === t.key ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
              }`}>
              {t.label}{t.key !== "todos" && counts_fecha_bucket ? ` (${counts_fecha_bucket[t.key] ?? 0})` : ""}
            </button>
          ))}
        </div>
      </div>

      {total === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-sm text-gray-400" data-testid="mi-trabajo-empty">
          Todo al día. Vuelve cuando haya algo nuevo que decidir.
        </div>
      ) : (
        <>
          {accionDirecta.length > 0 && (
            <div className="space-y-2" data-testid="mi-trabajo-seccion-accion-directa">
              {accionDirecta.map((it) => renderItemCard(it, `${it.tipo}:${it.id}`))}
            </div>
          )}
          {accionDirecta.length === 0 && revisionMasiva.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-sm text-gray-400" data-testid="mi-trabajo-sin-acciones-directas">
              Sin acciones directas pendientes con este filtro. Quedan {revisionMasiva.length} elemento{revisionMasiva.length === 1 ? "" : "s"} de previsión/revisión abajo.
            </div>
          )}
          {/* T11 · "evitar que 70 previsiones caducadas oculten tareas
              ejecutables" — esta sección agrupa previsiones/evidencias/notas
              de Comité (pueden ser decenas) aparte y PLEGADA por defecto. */}
          {revisionMasiva.length > 0 && (
            <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid="mi-trabajo-seccion-revision-masiva">
              <button
                onClick={() => setRevisionAbierta(v => !v)}
                data-testid="mi-trabajo-revision-masiva-toggle"
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-sm font-semibold text-gray-700 transition-colors"
              >
                <span className="flex items-center gap-2">
                  {revisionAbierta ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                  Previsión y revisión masiva
                </span>
                <span className="text-xs text-gray-400 font-normal">{revisionMasiva.length} elemento{revisionMasiva.length === 1 ? "" : "s"}</span>
              </button>
              {revisionAbierta && (
                <div className="p-3 space-y-2 bg-gray-50/50">
                  {revisionMasiva.map((it) => renderItemCard(it, `${it.tipo}:${it.id}`))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

